import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Mail } from "lucide-react";
import {
  getFlags,
  updateEntryInstructionEmailPrefs,
} from "../../store/thunks/hostActions";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

function EntryInstructionEmailPreference({
  hostId,
  title = "Delivery notifications",
  description = "Email me before every delivery that needs entry details or delivery context.",
  footerLink,
  className = "",
  onConfigured,
}) {
  const dispatch = useDispatch();
  const {
    receiveEntryInstructionsEmails,
    entryInstructionsAdditionalEmails = [],
    loading,
  } = useSelector((state) => state.hostFlags);

  const [newNotificationEmail, setNewNotificationEmail] = useState("");
  const [entryEmailError, setEntryEmailError] = useState("");
  const [entryEmailSuccess, setEntryEmailSuccess] = useState("");

  useEffect(() => {
    if (hostId) dispatch(getFlags(hostId));
  }, [dispatch, hostId]);

  const persistPreference = (payload, successMessage) => {
    if (!hostId) return;
    setEntryEmailError("");
    setEntryEmailSuccess("");
    dispatch(updateEntryInstructionEmailPrefs({ hid: hostId, ...payload })).then((result) => {
      if (result.meta.requestStatus === "fulfilled" && result.payload?.success) {
        setEntryEmailSuccess(result.payload.msg || successMessage || "Saved.");
        onConfigured?.();
      } else if (result.meta.requestStatus === "rejected" && result.payload?.error) {
        setEntryEmailError(result.payload.error);
      } else {
        setEntryEmailError("Could not save delivery notification preference.");
      }
    });
  };

  const handleToggleEntryInstructions = () => {
    persistPreference(
      {
        receiveEntryInstructionsEmails: !receiveEntryInstructionsEmails,
        entryInstructionsAdditionalEmails,
      },
      "Delivery notification preference saved."
    );
  };

  const handleAddNotificationEmail = () => {
    const email = newNotificationEmail.trim();
    if (!email) {
      setEntryEmailError("Enter an email address.");
      setEntryEmailSuccess("");
      return;
    }
    if (!isValidEmail(email)) {
      setEntryEmailError("Enter a valid email address.");
      setEntryEmailSuccess("");
      return;
    }
    if (entryInstructionsAdditionalEmails.includes(email)) {
      setEntryEmailError("This email is already in the list.");
      setEntryEmailSuccess("");
      return;
    }
    persistPreference(
      {
        receiveEntryInstructionsEmails,
        entryInstructionsAdditionalEmails: [...entryInstructionsAdditionalEmails, email],
      },
      "Email added."
    );
    setNewNotificationEmail("");
  };

  const handleRemoveNotificationEmail = (emailToRemove) => {
    persistPreference(
      {
        receiveEntryInstructionsEmails,
        entryInstructionsAdditionalEmails: entryInstructionsAdditionalEmails.filter((email) => email !== emailToRemove),
      },
      "Email removed."
    );
  };

  return (
    <section className={`rounded-2xl border border-gray-200 bg-white p-5 ${className}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-600">
            <Mail size={18} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">{title}</h3>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">{description}</p>
          </div>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={receiveEntryInstructionsEmails}
          onClick={handleToggleEntryInstructions}
          disabled={loading || !hostId}
          className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
            receiveEntryInstructionsEmails ? "bg-primary-500" : "bg-gray-200"
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
              receiveEntryInstructionsEmails ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>

      {receiveEntryInstructionsEmails && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Additional notification emails</p>
          {entryInstructionsAdditionalEmails.length > 0 && (
            <ul className="mt-3 space-y-2">
              {entryInstructionsAdditionalEmails.map((email) => (
                <li
                  key={email}
                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2"
                >
                  <span className="truncate text-sm text-gray-900">{email}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveNotificationEmail(email)}
                    disabled={loading}
                    className="rounded-lg px-2 py-1 text-xs font-semibold text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              type="email"
              value={newNotificationEmail}
              onChange={(event) => setNewNotificationEmail(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAddNotificationEmail();
                }
              }}
              placeholder="Add another email"
              disabled={loading}
              className="min-w-[220px] flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleAddNotificationEmail}
              disabled={loading}
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-bold text-white transition hover:bg-gray-800 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Add email"}
            </button>
          </div>
        </div>
      )}

      {entryEmailError && <p className="mt-3 text-sm text-red-600">{entryEmailError}</p>}
      {entryEmailSuccess && <p className="mt-3 text-sm text-green-600">{entryEmailSuccess}</p>}

      {footerLink && (
        <p className="mt-4 text-xs text-gray-500">
          You can also manage this in{" "}
          <Link to={footerLink.href} className="font-medium text-primary-600 hover:text-primary-700">
            {footerLink.label}
          </Link>
          .
        </p>
      )}
    </section>
  );
}

export default EntryInstructionEmailPreference;
